<template>
  <div v-if="!unavailable" class="mt-6">
    <h3 class="text-subtitle-1 font-weight-bold mb-2">
      {{ $t('supportRequests.unprocessed.title') }}
    </h3>

    <v-progress-linear v-if="loading" indeterminate color="deep-purple" class="mb-4" />

    <v-alert v-else-if="!items.length" type="info" text>
      {{ $t('supportRequests.unprocessed.empty') }}
    </v-alert>

    <template v-else>
      <v-card v-for="item in items" :key="item.id" class="mb-3" outlined>
        <v-card-text>
          <div class="d-flex align-center flex-wrap" style="gap: 8px">
            <support-request-ticket-chip :ticket-id="item.hubspotTicketId" />
            <support-request-school-chip
              :school-id="item.resolvedSchoolId"
              :school-name="item.resolvedSchoolName"
            />
            <span class="grey--text text-caption ml-auto">
              {{ $t('supportRequests.unprocessed.ticket_date', {
                date: formattedDate(item.ticketCreatedAt),
              }) }}
            </span>
          </div>

          <div v-if="item.subject" class="font-weight-bold mt-2">{{ item.subject }}</div>

          <v-alert dense text type="warning" class="mt-2 mb-2">
            <div class="text-caption font-weight-bold mb-1">
              {{ $t('supportRequests.unprocessed.reason_title') }}
            </div>
            {{ item.manualReason }}
          </v-alert>

          <div class="text-body-2 grey--text text--darken-2">
            {{ $t('supportRequests.unprocessed.form_data', {
              name: item.formStudentOrTeacherName || '—',
              dni: item.formDniOrRut || '—',
            }) }}
          </div>
        </v-card-text>
      </v-card>
    </template>

    <div v-if="totalPages > 1" class="text-center pt-2">
      <v-pagination
        v-model="page"
        :length="totalPages"
        circle
        :total-visible="7"
        @input="load"
      />
    </div>
  </div>
</template>

<script>
import supportRequestService from '@/services/supportRequest.service';
import SupportRequestTicketChip from './SupportRequestTicketChip.vue';
import SupportRequestSchoolChip from './SupportRequestSchoolChip.vue';

export default {
  name: 'SupportRequestUnprocessedList',
  components: { SupportRequestTicketChip, SupportRequestSchoolChip },
  props: {
    // Reusa los mismos filtros de colegio/fecha que la bandeja principal.
    filters: {
      type: Object,
      default: () => ({ schoolId: null, dateFrom: null, dateTo: null }),
    },
  },
  data: () => ({
    items: [],
    total: 0,
    page: 1,
    perPage: 10,
    loading: false,
    // Backend viejo sin este endpoint (404): se oculta la sección entera en
    // vez de romper la pantalla (KB-907, el endpoint puede llegar en otro PR).
    unavailable: false,
  }),
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.perPage));
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.page = 1;
        this.load();
      },
    },
  },
  created() {
    this.load();
  },
  methods: {
    formattedDate(date) {
      return date ? new Date(date).toLocaleDateString() : '—';
    },
    async load() {
      this.loading = true;
      try {
        const result = await supportRequestService.unprocessed({
          page: this.page,
          perPage: this.perPage,
          schoolId: this.filters.schoolId,
          dateFrom: this.filters.dateFrom,
          dateTo: this.filters.dateTo,
        });
        this.items = result.items;
        this.total = result.total;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.unavailable = true;
        } else {
          this.items = [];
          this.total = 0;
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
